// App.tsx - Complete heal Health AI Application
import React, { useState, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import './styles/globals.css';
import { Wifi, WifiOff, User, LogOut, Sun, Moon, Upload, Activity, CheckCircle, Shield, Eye, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { LandingPage } from './pages/LandingPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { EmailVerificationPage } from './pages/auth/EmailVerificationPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { ProfileView } from './components/dashboard/ProfileView';
import { AnalysisPage } from './pages/AnalysisPage';
import { ProcessingInfo } from './components/health/ProcessingInfo';
import { useToast, ToastContainer } from './components/ui/toast';
import { useAuth } from './hooks/useAuth';
import { healthService } from './services/healthService';
import { authService } from './services/authService';
import { apiService } from './services/api';
import { DashboardPage } from './pages/DashboardPage';
import { ExtractedResultsView } from './components/dashboard/ExtractedResultsView';


// Types
type AppView = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'email-verification' | 'dashboard' | 'profile' | 'analysis' | 'processing' | 'metrics-dashboard' | 'extracted-results' | 'category-detail' | 'categories' | 'guest_preview';
type DashboardView = 'health' | 'upload' | 'profile' | 'category-detail' | 'categories' | 'brain' | 'chat' | 'prescription';

interface ProcessingState {
  file: File;
  jobId: string;
  progress: number;
  stage: string;
  status: 'processing' | 'completed' | 'failed';
  errorCode?: string;
  errorType?: string;
  errorMessage?: string;
}

const App: React.FC = () => {
  // React Query
  const queryClient = useQueryClient();

  // Authentication
  const { isAuthenticated, user, login, register, logout, refreshUser, isLoading: authLoading } = useAuth();

  // App State
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [dashboardView, setDashboardView] = useState<DashboardView>('health');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  const [verificationEmail, setVerificationEmail] = useState('');
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [currentAnalysisData, setCurrentAnalysisData] = useState<any>(null);
  const [resetPasswordToken, setResetPasswordToken] = useState<string | null>(null);

  // Processing State
  const [processingState, setProcessingState] = useState<ProcessingState | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Background job tracking
  const [backgroundJobId, setBackgroundJobId] = useState<string | null>(null);

  // Dashboard refresh key - used to force remount of HealthDashboard when returning from preview
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  // Toast notifications
  const toast = useToast();

  // Check backend connectivity on mount
  useEffect(() => {
    checkInitialConnectivity();
    const interval = setInterval(checkConnectivity, 30000);
    return () => clearInterval(interval);
  }, []);

  // Set up global 401 unauthorized handler
  useEffect(() => {
    apiService.setUnauthorizedHandler(() => {
      // Clear local state
      logout();
      setCurrentView('login');
      setCurrentAnalysisData(null);
      setProcessingState(null);

      // Show user-friendly message
      toast.error('Your session has expired. Please log in again.');
    });
  }, []);

  // Check URL for password reset route and token
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Check query params first
    const urlParams = new URLSearchParams(window.location.search);
    let accessToken = urlParams.get('access_token') || urlParams.get('token');

    // Also check hash fragment - Supabase sends tokens in hash (e.g., #access_token=xxx)
    if (!accessToken && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      accessToken = hashParams.get('access_token') || hashParams.get('token');
      console.log('🔐 Checking hash fragment for token:', accessToken ? 'Found' : 'Not found');
    }

    // Check if user is on /reset-password route (from email link)
    if (currentPath === '/reset-password' && accessToken) {
      console.log('🔐 Reset password route detected with token');
      setResetPasswordToken(accessToken);
      setCurrentView('reset-password');

      // Clean URL query params and hash without reloading, keep the /reset-password path
      window.history.replaceState({}, document.title, '/reset-password');
    }
    // Fallback: Check for token on root URL (backward compatibility)
    else if (accessToken && currentPath === '/') {
      console.log('🔐 Reset password token detected on root URL');
      setResetPasswordToken(accessToken);
      setCurrentView('reset-password');

      // Clean URL without reloading
      window.history.replaceState({}, document.title, '/');
    }
  }, []);



  // Redirect to dashboard if authenticated (only after loading is complete)
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentView !== 'dashboard' && currentView !== 'metrics-dashboard' && currentView !== 'profile' && currentView !== 'analysis' && currentView !== 'processing' && currentView !== 'extracted-results' && currentView !== 'reset-password') {
      setCurrentView('metrics-dashboard');
    }
  }, [isAuthenticated, authLoading, currentView]);

  const checkInitialConnectivity = async () => {
    try {
      const response = await healthService.checkHealth();
      setIsConnected(response.success === true);
    } catch (error) {
      setIsConnected(false);
    }
  };

  // Refresh user data on mount/auth to ensure we have latest fields (like heal_id)
  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  const checkConnectivity = async () => {
    try {
      const response = await healthService.checkHealth();
      const newConnectionState = response.success === true;

      if (newConnectionState !== isConnected) {
        setIsConnected(newConnectionState);
        if (!newConnectionState) {
          toast.error('Connection lost', 3000);
        }
      } else {
        setIsConnected(newConnectionState);
      }
    } catch (error) {
      const newConnectionState = false;
      if (newConnectionState !== isConnected) {
        setIsConnected(newConnectionState);
        toast.error('Unable to connect to backend', 3000);
      } else {
        setIsConnected(newConnectionState);
      }
    }
  };

  // Authentication Handlers
  const handleLogin = async (credentials: any) => {
    try {
      await login(credentials);

      setIsReturningUser(true);

      setCurrentView('dashboard');
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle email not verified error - redirect to verification page
      if (error.code === 'EMAIL_NOT_VERIFIED' ||
        (error.message && error.message.toLowerCase().includes('email not verified'))) {
        setVerificationEmail(credentials.email);

        setCurrentView('email-verification');
        return; // Don't re-throw - we handled it
      }

      // Re-throw all other errors so LoginPage can display them
      throw error;
    }
  };

  const handleRegister = async (data: any) => {
    try {
      await register(data);
      setVerificationEmail(data.email);

      setCurrentView('email-verification');
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const handleEmailVerification = async (email: string, otp: string) => {
    try {
      const response = await authService.verifyEmail({
        email: email,
        otp: otp
      });

      if (response.success) {
        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token);
        }
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }



        setTimeout(() => {
          setCurrentView('dashboard');
        }, 1000);
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Verification failed');
      throw error;
    }
  };

  const handleResendVerification = async (email: string) => {
    try {
      const response = await authService.resendVerification(email);

      if (response.success) {

      } else {
        throw new Error(response.error?.message || 'Failed to resend');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast.error(error.message || 'Failed to resend verification email');
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
    setIsDemo(false);
    setCurrentView('login');
    setCurrentAnalysisData(null);
    setProcessingState(null);

  };

  const handleForgotPassword = async (email: string) => {
    try {
      const response = await authService.requestPasswordReset(email);

      if (response.success) {

      } else {
        throw new Error(response.error?.message || 'Failed to send reset link');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error(error.message || 'Failed to send reset link');
      throw error;
    }
  };

  const handleResetPassword = async (accessToken: string, newPassword: string) => {
    try {
      const response = await authService.resetPassword(accessToken, newPassword);

      if (response.success) {

      } else {
        throw new Error(response.error?.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const handleBackToUpload = () => {
    setCurrentView('dashboard');
    setCurrentAnalysisData(null);
  };

  // Determine content to render based on current view
  const renderContent = () => {
    // Check for maintenance mode
    if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
      return (
        <>
          <MaintenancePage />
          <Analytics />
        </>
      );
    }

    // Render Email Verification Page
    if (currentView === 'email-verification') {
      return (
        <EmailVerificationPage
          email={verificationEmail}
          onVerify={handleEmailVerification}
          onResend={handleResendVerification}
          onNavigateToLogin={() => setCurrentView('login')}
        />
      );
    }

    // Render Reset Password Page
    if (currentView === 'reset-password' && resetPasswordToken) {
      return (
        <ResetPasswordPage
          accessToken={resetPasswordToken}
          onResetPassword={handleResetPassword}
          onNavigateToLogin={() => setCurrentView('login')}
        />
      );
    }

    // Render Extracted Results View
    if (currentView === 'extracted-results') {
      if (!currentAnalysisData) {
        // Defer redirect to effect to avoid render loop, or just return null
        return null;
      }
      return (
        <ExtractedResultsView
          data={currentAnalysisData.data}
          jobId={backgroundJobId}
          onNavigateToDashboard={() => {
            if (!isAuthenticated) {
              setCurrentView('register');
            } else {
              queryClient.invalidateQueries({ queryKey: ['healthSummary'] });
              queryClient.invalidateQueries({ queryKey: ['categoryInsights'] });
              queryClient.invalidateQueries({ queryKey: ['userReports'] });
              setCurrentView('metrics-dashboard');
              setDashboardView('health');
              setDashboardRefreshKey(prev => prev + 1);
            }
          }}
        />
      );
    }

    // Render Profile Page
    if (currentView === 'profile') {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-background)',
          fontFamily: 'var(--font-primary)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: 'var(--space-6)'
          }}>
            <button
              onClick={() => setCurrentView('metrics-dashboard')}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-secondary)',
                marginBottom: 'var(--space-4)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ← Back to Dashboard
            </button>
            <ProfileView
              userEmail={user?.email || ''}
              userId={user?.id || ''}
              healId={user?.heal_id}
            />
          </div>
        </div>
      );
    }

    // Render Processing Page
    if (currentView === 'processing' && processingState) {
      return (
        <ProcessingInfo
          fileName={processingState.file.name}
          progress={processingState.progress}
          stage={processingState.stage}
          status={processingState.status}
          errorCode={processingState.errorCode}
          errorType={processingState.errorType}
          errorMessage={processingState.errorMessage}
          onRetry={() => {
            setProcessingState(null);
            setCurrentView('dashboard');
          }}
        />
      );
    }

    // Render Analysis Page
    if (currentView === 'analysis' && currentAnalysisData) {
      return (
        <AnalysisPage
          data={currentAnalysisData.data}
          onBack={handleBackToUpload}
        />
      );
    }

    // Render Dashboard with Navigation
    if (currentView === 'dashboard' || currentView === 'metrics-dashboard') {
      // Guard: ensure we have user data before rendering dashboard
      if ((!user || !user.id || !user.email) && !isDemo) {
        // Fallback to login if not authenticated and not in demo
        return <LandingPage
          onLogin={handleLogin}
          onRegister={handleRegister}
          onForgotPassword={handleForgotPassword}
        />;
      }

      return (
        <DashboardPage
          userId={user?.id || 'demo-user'}
          userEmail={user?.email || 'demo@healapp.co.in'}
          healId={user?.heal_id}
          onLogout={handleLogout}
          currentDashboardView={dashboardView}
          selectedCategory={selectedCategory}
          dashboardRefreshKey={dashboardRefreshKey}
          onNavigateToView={setDashboardView}
          isDemo={isDemo}
          isUploadModalOpen={isUploadModalOpen}
          onCategoryClick={(category) => {
            setSelectedCategory(category);
            setDashboardView('category-detail');
          }}
          onShowPreview={(data, jobId) => {
            setBackgroundJobId(jobId);
            setCurrentAnalysisData({ success: true, data });
            setCurrentView('extracted-results');
          }}
        />
      );
    }

    // Default: Render Landing Page
    return (
      <LandingPage
        onLogin={handleLogin}
        onRegister={handleRegister}
        onForgotPassword={handleForgotPassword}

      />
    );
  };

  return (
    <>
      {renderContent()}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
      <Analytics />


    </>
  );
};

export default App;